'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { CartApiService } from 'components/shared/Cart/services/CartApiService';
import { isCartMutateSuccess } from 'components/shared/Cart/utils';
import type { CartHydrationState } from 'components/shared/Cart/types';
import type { CartRequest } from 'lib/commerce/cart/types';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { RingProChargerCaseVariants } from 'content/products/ring-pro/ringProChargerCaseVariants';
import { BEST_BUY_PICKUP_ENABLED } from 'utils/featureFlags';
import { trackPickupEvent } from './pickupAnalytics';
import { createBestBuyHold, fetchBestBuyStores, type BestBuyFail } from './api';
import {
  BB_DATE_RE,
  BB_MAX_TOKEN_LENGTH,
  BB_PICKUP_GTM_EVENTS,
} from './config';
import {
  analyzePickupCart,
  buildClearMutation,
  buildPruneMutation,
  buildStampMutation,
  buildSwitchToShippingMutation,
  type ChargerVariantLookup,
  cartLinesSignature,
  getPickupLines,
  hasOrderReference,
  pickupKey,
} from './pickupCart';
import {
  HOLD_REUSE_MIN_MS,
  clearStoredHold,
  holdExpiresInMs,
  readStoredHold,
  releaseStoredHold,
  setLiveCartId,
  useStoredHold,
  writeStoredHold,
} from './pickupHold';
import {
  isPickupBusy,
  registerPickupCartActions,
  getPrunedNotice,
  getShipSwitchedNotice,
  setPickupBusy,
  setPickupNotice,
  setPrunedNotice,
  setShipSwitchedNotice,
  type PickupNotice,
} from './pickupNotice';
import { setPickupMode } from './pickupSelection';

type CartRecord = Record<string, any>;

/** Why pickup checkout stopped; the `reason` on cartCheckoutFailed. */
export type PickupBlockReason =
  | 'bb_pickup_multi_ring'
  | 'bb_pickup_store_lookup_failed'
  | 'bb_pickup_store_unavailable'
  | 'bb_pickup_hold_refused'
  | 'bb_pickup_cart_changed'
  | 'bb_pickup_stamp_failed'
  | 'bb_pickup_busy';

/** `cart` goes to checkout. `cart: null` stops it: with a `reason` it
 * failed (a notice says why), with `reason: null` it stopped by design
 * (the prune notice; the next tap goes on) and is not a failure. */
export type PrepareCheckoutResult =
  | { cart: CartRecord }
  | { cart: null; reason: PickupBlockReason | null; detail?: string };

const stopped = (
  reason: PickupBlockReason | null,
  detail?: string,
): PrepareCheckoutResult => ({
  cart: null,
  reason,
  ...(detail ? { detail } : {}),
});

const usChargerVariant: ChargerVariantLookup = (caseColor, size) =>
  RingProChargerCaseVariants[caseColor]?.[size]?.variantId[ShopifyStore.US];

/** The analytics `reason_detail`: our own request cap ('timeout') stays
 * apart from a Best Buy outage in GA4. The notice is unchanged. */
const failDetail = (failure: BestBuyFail): string =>
  failure.code === 'timeout' ? 'timeout' : failure.reason;

const noticeFor = (failure: BestBuyFail): PickupNotice => {
  switch (failure.reason) {
    case 'unprocessable':
      return { kind: 'unavailable', message: failure.message };
    case 'unreachable':
      return { kind: 'unreachable' };
    case 'rate_limited':
      return { kind: 'rate_limited' };
    default:
      return { kind: 'error' };
  }
};

// setTimeout cannot take more than 2^31 - 1 ms.
const MAX_TIMEOUT_MS = 2_147_483_647;

/** The hold whose pickup checkout was last tracked (see prepareLocked). */
let lastTrackedHold: string | null = null;

/** Best Buy pickup glue for CartProvider: the hold before checkout, the
 * switch back to shipping, and releasing a hold nobody needs. With the flag
 * off every effect and `prepareCheckout` return immediately. */
export function useBestBuyPickupCart(args: {
  cart: CartRecord | null;
  store: ShopifyStore | null;
  setCart: (cart: CartRecord | null) => void;
  cartHydrationState: CartHydrationState;
}) {
  const { cart, store, setCart, cartHydrationState } = args;
  const cartRef = useRef(cart);
  cartRef.current = cart;
  // Before any effect below can release a hold (effects run in order).
  useEffect(() => {
    setLiveCartId(typeof cart?.id === 'string' ? cart.id : null);
  }, [cart?.id]);
  const api = useMemo(
    () => (store ? new CartApiService(store) : null),
    [store],
  );

  const mutate = useCallback(
    async (
      base: CartRecord,
      request: Omit<CartRequest, 'cartId'>,
    ): Promise<CartRecord | null> => {
      if (!api || !base.id) return null;
      try {
        const response = await api.mutateCart({ cartId: base.id, ...request });
        if (!isCartMutateSuccess(response) || !response.cart) return null;
        setCart(response.cart as CartRecord);
        return response.cart as CartRecord;
      } catch {
        return null;
      }
    },
    [api, setCart],
  );

  /** Resolves the cart to send to checkout, or why checkout must stop
   * (a notice explains it and offers shipping). */
  const prepareLocked = useCallback(
    async (current: CartRecord): Promise<PrepareCheckoutResult> => {
      // Checkout starts: the "switched to shipping" and "removed N items"
      // messages have done their job.
      setShipSwitchedNotice(null);
      setPrunedNotice(null);
      const verdict = analyzePickupCart(current);

      if (verdict.kind === 'none') {
        // A shipping order must not carry a stale hold reference.
        releaseStoredHold();
        setPickupNotice(null);
        const clear = hasOrderReference(current)
          ? buildClearMutation(current)
          : null;
        return {
          cart: clear ? (await mutate(current, clear)) ?? current : current,
        };
      }
      if (verdict.kind === 'multi') {
        // One pickup ring with other items next to it (a cart from before
        // the exclusivity rule, or filled in another tab): the other items
        // leave now, before any Best Buy hold. Checkout stops this once so
        // the shopper sees what changed; the next tap goes on.
        const prune = buildPruneMutation(current);
        if (prune) {
          const pruned = await mutate(current, prune.mutation);
          if (pruned) {
            setPickupNotice(null);
            // A quantity reset alone removed nothing: no "Removed N items".
            if (prune.removedLineIds.length > 0) {
              setPrunedNotice({
                count: prune.removedLineIds.length,
                signature: cartLinesSignature(pruned),
              });
            }
            const [line] = getPickupLines(pruned);
            trackPickupEvent(BB_PICKUP_GTM_EVENTS.PRUNED, {
              entry: 'buy_page',
              removed_count: prune.removedLineIds.length,
              location_id: line?.locationId ?? '',
              sku: line?.sku ?? '',
            });
            return stopped(null);
          }
        }
        setPickupNotice({ kind: 'multi' });
        return stopped('bb_pickup_multi_ring');
      }

      setPickupNotice(null);
      const { line } = verdict;
      let hold = readStoredHold();
      const reusable =
        hold !== null &&
        hold.location_id === line.locationId &&
        hold.sku === line.sku &&
        hold.cart_id === current.id &&
        holdExpiresInMs(hold) > HOLD_REUSE_MIN_MS;

      if (!reusable) {
        if (hold) releaseStoredHold();
        // A fresh token every time: they are short-lived.
        const stores = await fetchBestBuyStores({
          postalCode: line.zip,
          items: [{ sku: line.sku, quantity: 1 }],
        });
        if (!stores.ok) {
          setPickupNotice(noticeFor(stores));
          return stopped('bb_pickup_store_lookup_failed', failDetail(stores));
        }
        const match = stores.data.stores.find(
          (s) => s.location_id === line.locationId && s.allItemsAvailable,
        );
        const token =
          match?.items.find((i) => i.sku === line.sku)?.availabilityToken ??
          match?.availabilityTokens[0];
        if (!match || !token || token.length > BB_MAX_TOKEN_LENGTH) {
          setPickupNotice({ kind: 'unavailable' });
          return stopped('bb_pickup_store_unavailable');
        }
        const created = await createBestBuyHold({
          sku: line.sku,
          locationId: line.locationId,
          availabilityToken: token,
        });
        if (!created.ok) {
          setPickupNotice(noticeFor(created));
          return stopped('bb_pickup_hold_refused', failDetail(created));
        }
        hold = {
          order_reference: created.data.orderReference,
          expires_at: created.data.expiresAt,
          location_id: created.data.locationId,
          sku: created.data.sku,
          ...(created.data.promiseDate &&
          BB_DATE_RE.test(created.data.promiseDate)
            ? { promise_date: created.data.promiseDate }
            : {}),
          ...(current.id ? { cart_id: current.id } : {}),
        };
        writeStoredHold(hold);
      }

      if (!hold) return stopped('bb_pickup_hold_refused');
      // The cart may have changed during the awaits above (a switch to
      // shipping, a removed ring, a gift toggle): stamp from the fresh cart,
      // and only if it is still the same pickup line.
      const fresh = cartRef.current ?? current;
      const again = analyzePickupCart(fresh);
      if (
        again.kind !== 'ok' ||
        again.line.lineId !== line.lineId ||
        again.line.locationId !== line.locationId ||
        again.line.sku !== line.sku
      ) {
        releaseStoredHold();
        return stopped('bb_pickup_cart_changed');
      }
      const stamped = await mutate(
        fresh,
        // The pickup order is the ring alone: a legacy charger line leaves in
        // the same request that stamps the hold.
        buildStampMutation(
          fresh,
          again.line,
          hold.order_reference,
          again.companionLineIds,
        ),
      );
      if (!stamped) {
        // The hold stays stored: the retry reuses it.
        setPickupNotice({ kind: 'error' });
        return stopped('bb_pickup_stamp_failed');
      }
      // Marks a checkout that leaves as pickup (the share of ring checkouts
      // choosing pickup); begin_checkout follows for the same cart. Once per
      // hold, so a retry or a return from checkout is not counted again.
      if (lastTrackedHold !== hold.order_reference) {
        lastTrackedHold = hold.order_reference;
        trackPickupEvent(BB_PICKUP_GTM_EVENTS.CHECKOUT, {
          entry: 'buy_page',
          location_id: again.line.locationId,
          sku: again.line.sku,
          // Joins the event to the Shopify order, which carries the same
          // reference as a note attribute.
          order_reference: hold.order_reference,
        });
      }
      return { cart: stamped };
    },
    [mutate],
  );

  const prepareCheckout = useCallback(
    async (current: CartRecord): Promise<PrepareCheckoutResult> => {
      if (!BEST_BUY_PICKUP_ENABLED) return { cart: current };
      // A switch to shipping is mid-flight: stop, the shopper can retry.
      if (isPickupBusy()) return stopped('bb_pickup_busy');
      setPickupBusy(true);
      try {
        return await prepareLocked(current);
      } finally {
        setPickupBusy(false);
      }
    },
    [prepareLocked],
  );

  const switchToShipping = useCallback(async () => {
    if (isPickupBusy()) return;
    setPickupBusy(true);
    try {
      const current = cartRef.current;
      if (current) {
        const plan = buildSwitchToShippingMutation(current, usChargerVariant);
        if (!plan.ok) {
          // No charger variant to add back: change nothing, say so.
          setPickupNotice({ kind: 'error' });
          return;
        }
        let next: CartRecord | null = current;
        if (plan.mutation) {
          next = await mutate(current, plan.mutation);
          if (!next) {
            // The cart still carries the pickup properties: say so.
            setPickupNotice({ kind: 'error' });
            return;
          }
        }
        setShipSwitchedNotice(
          plan.chargerCase
            ? {
                chargerCase: plan.chargerCase,
                signature: cartLinesSignature(next),
              }
            : null,
        );
      }
      setPickupNotice(null);
      setPickupMode('ship');
      releaseStoredHold();
    } finally {
      setPickupBusy(false);
    }
  }, [mutate]);

  useEffect(() => {
    if (!BEST_BUY_PICKUP_ENABLED) return;
    registerPickupCartActions({ switchToShipping });
    return () => registerPickupCartActions(null);
  }, [switchToShipping]);

  // The pickup line went away or changed (removed, cart emptied, switched to
  // shipping): free the hold. Only a change seen after hydration counts, so a
  // fresh load with a stored hold and an empty cart keeps it for reuse.
  const previousKey = useRef<string | null>(null);
  useEffect(() => {
    if (!BEST_BUY_PICKUP_ENABLED || cartHydrationState !== 'ready') return;
    const key = pickupKey(cart);
    if (previousKey.current && key !== previousKey.current) {
      const hold = readStoredHold();
      if (hold && key !== `${hold.location_id}|${hold.sku}`)
        releaseStoredHold();
    }
    // Any change to the cart's lines retires the "switched to shipping" message.
    const switched = getShipSwitchedNotice();
    if (switched && switched.signature !== cartLinesSignature(cart)) {
      setShipSwitchedNotice(null);
    }
    const pruned = getPrunedNotice();
    if (pruned && pruned.signature !== cartLinesSignature(cart)) {
      setPrunedNotice(null);
    }
    if (!key) setPickupNotice(null);
    else if (analyzePickupCart(cart).kind === 'ok') setPickupNotice(null);
    previousKey.current = key;
  }, [cart, cartHydrationState]);

  // Expiry: forget the hold and take the reference off the cart; the next
  // checkout holds again.
  const hold = useStoredHold();
  const holdReference = hold?.order_reference;
  const holdExpiresAt = hold?.expires_at;
  useEffect(() => {
    if (!BEST_BUY_PICKUP_ENABLED || !holdReference || !holdExpiresAt) return;
    const expire = () => {
      clearStoredHold();
      const current = cartRef.current;
      const clear = current ? buildClearMutation(current) : null;
      if (current && clear) void mutate(current, clear);
    };
    const ms = new Date(holdExpiresAt).getTime() - Date.now();
    if (ms <= 0) {
      expire();
      return;
    }
    const timer = setTimeout(expire, Math.min(ms, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [holdReference, holdExpiresAt, mutate]);

  return { prepareCheckout };
}
